import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SearchOutlined, UserAddOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Row, Spin, Switch, Table, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { EUserRole } from "@devowl-wp/api-real-cookie-banner";
import { postPreReleaseClient, updatePreReleaseClient } from "../../../../api/real-cookie-banner.js";
import { usePreReleaseClients } from "../../../../hooks/usePreReleaseClients.js";
import { useTranslation } from "../../../../others/i18n.js";
import { useStores } from "../../../../store/root.js";
import { ForbiddenInfo } from "../../forbidden.js";
import { PermissionCheck } from "../user/permission.js";
function PreRelease() {
    // Generals
    const { t } = useTranslation();
    const { rcbAuthenticationStore, notificationStore } = useStores();
    // Permission Checks
    const hasRole = rcbAuthenticationStore.hasRole(EUserRole.Admin);
    if (!hasRole) {
        return /*#__PURE__*/ _jsx(ForbiddenInfo, {});
    }
    // States
    const [searchTerm, setSearchTerm] = useState("");
    // Query
    const clientsQuery = usePreReleaseClients(searchTerm);
    // UI elements
    const { Title } = Typography;
    // Columns definition
    const columns = [
        {
            title: t("Client UUID"),
            dataIndex: "clientUuid",
            key: "clientUuid",
            defaultSortOrder: "ascend",
            sorter: (a, b)=>a.clientUuid.localeCompare(b.clientUuid)
        },
        {
            title: t("Enabled"),
            dataIndex: "isEnabled",
            key: "isEnabled",
            sorter: (a, b)=>a.isEnabled === b.isEnabled ? 0 : a.isEnabled ? -1 : 1,
            onFilter: (value, record)=>record.isEnabled === Boolean(value),
            filterSearch: true,
            filters: [
                {
                    text: t("activated"),
                    value: true
                },
                {
                    text: t("deactivated"),
                    value: false
                }
            ],
            render: (_, { clientUuid, isEnabled })=>{
                return /*#__PURE__*/ _jsx(Switch, {
                    onChange: async (isEnabled)=>{
                        try {
                            await updatePreReleaseClient({
                                headers: {
                                    Authorization: `Bearer ${rcbAuthenticationStore?.session?.jwt}`
                                },
                                body: {
                                    preReleaseClient: {
                                        clientUuid,
                                        isEnabled
                                    }
                                }
                            });
                            clientsQuery.refetch();
                            notificationStore.add({
                                type: "success",
                                message: t("Updated client {{clientId}}", {
                                    clientId: clientUuid
                                }),
                                duration: 3.5
                            });
                        } catch (error) {
                            notificationStore.addFromServerError(error, t);
                        }
                    },
                    checked: isEnabled
                });
            },
            align: "right"
        }
    ];
    useEffect(()=>{
        if (clientsQuery.error) {
            notificationStore.addFromServerError(clientsQuery.error, t);
        }
    }, [
        clientsQuery
    ]);
    const [addEntryForm] = Form.useForm();
    const [searchForm] = Form.useForm();
    const submitAddEntry = useCallback(async (client)=>{
        try {
            await postPreReleaseClient({
                headers: {
                    Authorization: `Bearer ${rcbAuthenticationStore?.session?.jwt}`
                },
                body: {
                    preReleaseClient: client
                }
            });
            clientsQuery.refetch();
            notificationStore.add({
                type: "success",
                message: t("Added client {{uuid}}", {
                    uuid: client.clientUuid
                }),
                duration: 3.5,
                closable: true
            });
            addEntryForm.resetFields();
            searchForm.resetFields();
            setSearchTerm("");
        } catch (error) {
            notificationStore.addFromServerError(error, t);
        }
    }, []);
    const title = t("Pre Release Clients");
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: title
                })
            }),
            /*#__PURE__*/ _jsxs(PermissionCheck, {
                minRole: EUserRole.Admin,
                children: [
                    /*#__PURE__*/ _jsx(Row, {
                        children: /*#__PURE__*/ _jsx(Col, {
                            flex: 1,
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 3,
                                children: title
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Row, {
                        children: /*#__PURE__*/ _jsx(Col, {
                            flex: 1,
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 5,
                                children: t("Add client by UUID")
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Row, {
                        children: [
                            /*#__PURE__*/ _jsx(Col, {
                                flex: 3,
                                style: {
                                    float: "left"
                                },
                                children: /*#__PURE__*/ _jsxs(Form, {
                                    form: addEntryForm,
                                    initialValues: {
                                        clientUuid: "",
                                        isEnabled: true
                                    },
                                    name: "add_entry",
                                    layout: "inline",
                                    onFinish: (values)=>submitAddEntry(values),
                                    children: [
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            name: "clientUuid",
                                            rules: [
                                                {
                                                    pattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/,
                                                    message: t("Given format must match uuid format (v4)")
                                                }
                                            ],
                                            children: /*#__PURE__*/ _jsx(Input, {
                                                prefix: /*#__PURE__*/ _jsx(UserAddOutlined, {}),
                                                placeholder: t("Client UUID"),
                                                style: {
                                                    minWidth: "22rem"
                                                }
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            name: "isEnabled",
                                            valuePropName: "checked",
                                            children: /*#__PURE__*/ _jsx(Switch, {
                                                checkedChildren: t("On"),
                                                unCheckedChildren: t("Off")
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                type: "primary",
                                                htmlType: "submit",
                                                children: t("Add")
                                            })
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(Col, {
                                flex: "auto"
                            }),
                            /*#__PURE__*/ _jsx(Col, {
                                flex: 2,
                                children: /*#__PURE__*/ _jsxs(Form, {
                                    form: searchForm,
                                    initialValues: {
                                        searchTerm: ""
                                    },
                                    name: "search",
                                    layout: "inline",
                                    onFinish: (data)=>{
                                        setSearchTerm(data.searchTerm);
                                    },
                                    style: {
                                        float: "right"
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            name: "searchTerm",
                                            rules: [
                                                {
                                                    pattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/,
                                                    message: t("Given format must match uuid format (v4)")
                                                }
                                            ],
                                            children: /*#__PURE__*/ _jsx(Input, {
                                                prefix: /*#__PURE__*/ _jsx(SearchOutlined, {
                                                    placeholder: t("Client UUID")
                                                }),
                                                style: {
                                                    minWidth: "22rem"
                                                },
                                                placeholder: t("Search by client UUID")
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                type: "primary",
                                                htmlType: "submit",
                                                children: t("Search")
                                            })
                                        })
                                    ]
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Row, {
                        style: {
                            paddingTop: "1rem"
                        },
                        children: /*#__PURE__*/ _jsx(Col, {
                            flex: 5,
                            children: /*#__PURE__*/ _jsx(Spin, {
                                spinning: clientsQuery.isFetching,
                                children: /*#__PURE__*/ _jsx(Table, {
                                    dataSource: clientsQuery?.data?.preReleaseClients,
                                    columns: columns,
                                    pagination: false,
                                    rowKey: "clientUuid"
                                })
                            })
                        })
                    })
                ]
            })
        ]
    });
}
export { PreRelease };
